import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import firebase from "../../firebase";
import { useBarcode } from 'react-barcodes';
import { modes, paymentMethods, paymentUtilities } from "../../enums";

const db = firebase.firestore();

function PrintEN() {
    const { id } = useParams<{ id: string }>();
    const [trackId] = useState<string>(id);
    const { inputRef } = useBarcode({
        value: trackId,
        options: { height: 42, width: 1.5, displayValue: false, margin: 0 },
    });

    const [senderPhone, setSenderPhone] = useState("");
    const [senderName, setSenderName] = useState("");
    const [senderAddress, setSenderAddress] = useState("");
    const [receiverPhone, setReceiverPhone] = useState("");
    const [receiverName, setReceiverName] = useState("");
    const [receiverAddress, setReceiverAddress] = useState("");
    const [weight, setWeight] = useState<any>("");
    const [netWeight, setNetWeight] = useState<any>("");
    const [quantity, setQuantity] = useState<any>("");
    const [mode, setMode] = useState("");
    const [price, setPrice] = useState<any>("");
    const [paymentMethod, setPaymentMethod] = useState("");
    const [hasDelivery, setHasDelivery] = useState("");
    const [desc, setDesc] = useState("");
    const [value, setValue] = useState("");
    const [departTime, setDepartTime] = useState("");
    const [paymentUtility, setPaymentUtility] = useState("");
    const [docRef] = useState<firebase.firestore.DocumentReference>(
        db.collection("packages").doc(id)
    );

    useEffect(() => {
        docRef.get().then((doc) => {
            if (doc.exists) {
                const data = doc.data();
                setSenderName(data?.senderName || "Khishig Cargo Korea");
                setSenderPhone(data?.senderPhone || "+82 10-9802-7555");
                setSenderAddress(data?.senderAddress || "#501, 25, Digital-ro 33-gil, Guro-gu, Seoul, Korea");
                setReceiverName(data?.receiverName || "");
                setReceiverPhone(data?.receiverPhone || "");
                setReceiverAddress(data?.receiverAddress || "");
                setWeight(data?.weight || "");
                setNetWeight(data?.netWeight || data?.weight || "");
                setPrice(data?.price || "");
                setPaymentMethod(data?.paymentMethod || "");
                setHasDelivery(data?.hasDelivery || "");
                setMode(data?.mode || Object.keys(modes)[0]);
                setQuantity(data?.quantity || "");
                setDesc(data?.desc || "");
                setValue(data?.value || "");
                setDepartTime(data?.departTime || "");
                setPaymentUtility(data?.paymentUtility || "");
            }
        }).catch(console.error);
    }, [id, docRef]);

    const getServiceType = (m: string) => {
        const map: any = {
            express: "EXPRESS",
            normal: "STANDARD",
            air: "AIR CARGO",
            warm: "WARM EXPRESS",
            international: "INTERNATIONAL",
            mobile: "MOBILE",
        };
        return map[m] || (m ? m.toUpperCase() : "EXPRESS");
    };

    const formatPrice = (p: any) => {
        if (!p) return "-";
        const stripped = String(p).replace(/[₩,\s]/g, '');
        const num = parseFloat(stripped);
        return !isNaN(num) ? num.toLocaleString() : String(p);
    };

    const isPrepaid = ["paid", "paidInUlaanbaatar"].includes(paymentMethod);
    const isCollect = ["mustPaid"].includes(paymentMethod);
    const isPickup = hasDelivery === "noDelivery";
    const isDelivery = hasDelivery === "delivery";

    const bdr = "0.5px solid #000";

    const chk = (checked: boolean): React.CSSProperties => ({
        width: "4mm",
        height: "4mm",
        border: "1.5px solid #000",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "8pt",
        fontWeight: "bold",
        flexShrink: 0,
        backgroundColor: "#fff",
    });

    return (
        <>
            <style>{`
                * { box-sizing: border-box; margin: 0; padding: 0; }
                * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                .print-overlay {
                    position: fixed; top: 0; left: 0;
                    width: 100vw; height: 100vh;
                    background: white; z-index: 9999;
                    display: flex; align-items: center; justify-content: center;
                }
                @media print {
                    @page { margin: 0; }
                    html, body {
                        margin: 0; padding: 0;
                        width: 100vw; height: 100vh;
                        overflow: hidden;
                    }
                    .print-overlay {
                        position: absolute;
                        top: 0; left: 0;
                        width: 100vw;
                        height: 100vh;
                        display: block;
                        overflow: hidden;
                    }
                    .label-root {
                        position: absolute;
                        top: 50vh;
                        left: 50vw;
                        width: 150mm;
                        height: 100mm;
                        transform-origin: center center;
                        transform:
                            translate(-50%, -50%)
                            rotate(-90deg)
                            scale(min(calc(100vh / 567px), calc(100vw / 378px)));
                    }
                }
            `}</style>

            <div className="print-overlay">
            <div className="label-root" style={{
                width: "150mm",
                height: "100mm",
                fontFamily: "Arial, Helvetica, sans-serif",
                color: "#000",
                backgroundColor: "#fff",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                border: bdr,
            }}>

                {/* ── ROW 1: HEADER ─────────────────────────────────── */}
                <div style={{ display: "flex", borderBottom: bdr, height: "28mm", flexShrink: 0 }}>

                    {/* Logo */}
                    <div style={{ width: "33mm", borderRight: bdr, padding: "2mm 2mm 1.5mm 2mm", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                        <img src="/logo.svg" alt="Хишиг Карго" style={{ width: "29mm", height: "auto" }} />
                        <div style={{ fontSize: "5.5pt", lineHeight: 1.4 }}>
                            Track your shipment:<br />
                            <strong style={{ fontSize: "6pt" }}>www.khishigcargo.com</strong>
                        </div>
                    </div>

                    {/* Tracking No. + Barcode */}
                    <div style={{ flex: 1, borderRight: bdr, padding: "1.5mm 2mm 1mm 2mm", display: "flex", flexDirection: "column" }}>
                        <div style={{ display: "inline-block", backgroundColor: "#000", color: "#fff", fontWeight: "bold", fontSize: "6pt", padding: "0.4mm 1.5mm", marginBottom: "0.5mm", alignSelf: "flex-start" }}>
                            TRACKING NO.
                        </div>
                        <div style={{ fontSize: "17pt", fontWeight: "bold", lineHeight: 1, letterSpacing: "0.3pt", textAlign: "center" }}>
                            {trackId}
                        </div>
                        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <svg ref={inputRef} style={{ maxWidth: "100%", display: "block" }} />
                        </div>
                        <div style={{ textAlign: "center", fontSize: "6pt" }}>{trackId}</div>
                    </div>

                    {/* Package + Service Type */}
                    <div style={{ width: "36mm", display: "flex", flexDirection: "column", borderRight: bdr }}>
                        <div style={{ flex: 1, borderBottom: bdr, padding: "1mm", textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                            <div style={{ backgroundColor: "#000", color: "#fff", fontWeight: "bold", fontSize: "5.5pt", padding: "0.3mm 2mm", marginBottom: "1mm" }}>
                                PACKAGE
                            </div>
                            <div style={{ fontSize: "15pt", fontWeight: "bold", lineHeight: 1 }}>1 of {quantity || "?"}</div>
                            <div style={{ fontSize: "5pt", marginTop: "0.5mm", color: "#333" }}>( Нийт {quantity || "?"} хайрцаг )</div>
                        </div>
                        <div style={{ flex: 1, padding: "1mm", textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                            <div style={{ backgroundColor: "#000", color: "#fff", fontWeight: "bold", fontSize: "5.5pt", padding: "0.3mm 2mm", marginBottom: "1mm" }}>
                                SERVICE TYPE
                            </div>
                            <div style={{ fontSize: "12pt", fontWeight: "bold" }}>{getServiceType(mode)}</div>
                        </div>
                    </div>
                </div>

                {/* ── ROW 2: FROM / TO / DELIVERY ───────────────────── */}
                <div style={{ display: "flex", flexDirection: "column", height: "38mm", flexShrink: 0 }}>

                    {/* Shared header row */}
                    <div style={{ display: "flex", borderBottom: bdr, flexShrink: 0 }}>
                        <div style={{ flex: 1, borderRight: bdr, padding: "1mm" }}>
                            <div style={{ backgroundColor: "#000", color: "#fff", fontWeight: "bold", fontSize: "5.5pt", padding: "0.3mm 2mm", textAlign: "center" }}>
                                FROM (ИЛГЭЭГЧ)
                            </div>
                        </div>
                        <div style={{ flex: 1, borderRight: bdr, padding: "1mm" }}>
                            <div style={{ backgroundColor: "#000", color: "#fff", fontWeight: "bold", fontSize: "5.5pt", padding: "0.3mm 2mm", textAlign: "center" }}>
                                TO (ХҮЛЭЭН АВАГЧ)
                            </div>
                        </div>
                        <div style={{ width: "36mm", borderRight: bdr, padding: "1mm" }}>
                            <div style={{ backgroundColor: "#000", color: "#fff", fontWeight: "bold", fontSize: "5.5pt", padding: "0.3mm 2mm", textAlign: "center" }}>
                                DELIVERY OPTION
                            </div>
                        </div>
                    </div>

                    {/* Top content: Name+Phone | Name+Phone | DELIVERY OPTION */}
                    <div style={{ display: "flex", flex: 1, borderBottom: bdr }}>
                        <div style={{ flex: 1, borderRight: bdr, display: "flex", flexDirection: "column" }}>
                            <div style={{ display: "flex", borderBottom: bdr, fontSize: "6pt", minHeight: "9mm", padding: "0.8mm 0", alignItems: "center" }}>
                                <span style={{ padding: "0 2mm", whiteSpace: "nowrap", width: "12mm", fontWeight: "bold" }}>Name:</span>
                                <strong style={{ padding: "0 1mm", fontSize: "8pt" }}>{senderName}</strong>
                            </div>
                            <div style={{ display: "flex", flex: 1, fontSize: "6pt", padding: "0.8mm 0" }}>
                                <span style={{ padding: "0 2mm", whiteSpace: "nowrap", width: "12mm", fontWeight: "bold" }}>Phone:</span>
                                <strong style={{ padding: "0 1mm", fontSize: "8pt" }}>{senderPhone}</strong>
                            </div>
                        </div>
                        <div style={{ flex: 1, borderRight: bdr, display: "flex", flexDirection: "column" }}>
                            <div style={{ display: "flex", borderBottom: bdr, fontSize: "6pt", minHeight: "9mm", padding: "0.8mm 0", alignItems: "center" }}>
                                <span style={{ padding: "0 2mm", whiteSpace: "nowrap", width: "12mm", fontWeight: "bold" }}>Name:</span>
                                <strong style={{ padding: "0 1mm", fontSize: "8pt" }}>{receiverName}</strong>
                            </div>
                            <div style={{ display: "flex", flex: 1, fontSize: "6pt", padding: "0.8mm 0" }}>
                                <span style={{ padding: "0 2mm", whiteSpace: "nowrap", width: "12mm", fontWeight: "bold" }}>Phone:</span>
                                <strong style={{ padding: "0 1mm", fontSize: "8pt" }}>{receiverPhone}</strong>
                            </div>
                        </div>
                        {/* Delivery Option */}
                        <div style={{ width: "36mm", borderRight: bdr, padding: "1mm", textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                            <div style={{ display: "flex", justifyContent: "space-around", padding: "1mm 1mm 0.5mm" }}>
                                <div style={{ textAlign: "center" }}>
                                    <div style={{ lineHeight: 1 }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="13pt" height="13pt" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                            <path d="M12 4a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
                                            <path d="M7 21l3 -4" />
                                            <path d="M16 21l-2 -4l-3 -3l1 -6" />
                                            <path d="M6 12l2 -3l4 -1l3 3l3 1" />
                                        </svg>
                                    </div>
                                    <div style={{ fontSize: "5.5pt", fontWeight: "bold" }}>PICKUP</div>
                                    <div style={{ fontSize: "4.5pt", color: "#444" }}>(Очиж авах)</div>
                                    <div style={{ ...chk(isPickup), margin: "0.5mm auto 0" }}>
                                        {isPickup && "✓"}
                                    </div>
                                </div>
                                <div style={{ textAlign: "center" }}>
                                    <div style={{ lineHeight: 1 }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="13pt" height="13pt" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                            <path d="M5 17a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                                            <path d="M15 17a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                                            <path d="M5 17h-2v-4m-1 -8h11v12m-4 0h6m4 0h2v-6h-8m0 -5h5l3 5" />
                                            <path d="M3 9l4 0" />
                                        </svg>
                                    </div>
                                    <div style={{ fontSize: "5.5pt", fontWeight: "bold" }}>DELIVERY</div>
                                    <div style={{ fontSize: "4.5pt", color: "#444" }}>(Хүргэлтээр авах)</div>
                                    <div style={{ ...chk(isDelivery), margin: "0.5mm auto 0" }}>
                                        {isDelivery && "✓"}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom content: Address | Address | SHIPPING FEE */}
                    <div style={{ display: "flex", flex: 1 }}>
                        <div style={{ flex: 1, borderRight: bdr, display: "flex", fontSize: "6pt", padding: "0.8mm 0" }}>
                            <span style={{ padding: "0 2mm", whiteSpace: "nowrap", width: "12mm", paddingTop: "0.3mm", fontWeight: "bold" }}>Address:</span>
                            <strong style={{ padding: "0 1mm", fontSize: "8pt" }}>{senderAddress}</strong>
                        </div>
                        <div style={{ flex: 1, borderRight: bdr, display: "flex", fontSize: "6pt", padding: "0.8mm 0" }}>
                            <span style={{ padding: "0 2mm", whiteSpace: "nowrap", width: "12mm", paddingTop: "0.3mm", fontWeight: "bold" }}>Address:</span>
                            <strong style={{ padding: "0 1mm", fontSize: "8pt" }}>{receiverAddress}</strong>
                        </div>
                        <div style={{ width: "36mm", borderRight: bdr, padding: "1mm 2mm", textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                            <div style={{ fontSize: "5.5pt", fontWeight: "bold" }}>SHIPPING FEE</div>
                            <div style={{ fontSize: "13pt", fontWeight: "bold", lineHeight: 1.1 }}>
                                ₩ {formatPrice(price)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── ROW 3: CUSTOMS DECLARATION ────────────────────── */}
                <div style={{ flexShrink: 0, borderTop: bdr }}>
                    <div style={{ padding: "1mm" }}>
                        <div style={{ backgroundColor: "#000", color: "#fff", fontWeight: "bold", fontSize: "5.5pt", padding: "0.3mm 2mm", textAlign: "center" }}>
                            CUSTOMS DECLARATION (CN23)
                        </div>
                    </div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "5.5pt", border: bdr }}>
                        <thead>
                            <tr>
                                <th style={{ borderRight: bdr, borderBottom: bdr, padding: "0.5mm 1mm", textAlign: "center", fontWeight: "bold", width: "27%" }}>DESCRIPTION OF CONTENTS</th>
                                <th style={{ borderRight: bdr, borderBottom: bdr, padding: "0.5mm 1mm", textAlign: "center", fontWeight: "bold", width: "11%" }}>QTY (PCS)</th>
                                <th style={{ borderRight: bdr, borderBottom: bdr, padding: "0.5mm 1mm", textAlign: "center", fontWeight: "bold", width: "14%" }}>NET WEIGHT (Kg)</th>
                                <th style={{ borderRight: bdr, borderBottom: bdr, padding: "0.5mm 1mm", textAlign: "center", fontWeight: "bold", width: "13%" }}>VALUE (MNT)</th>
                                <th style={{ borderRight: bdr, borderBottom: bdr, padding: "0.5mm 1mm", textAlign: "center", fontWeight: "bold", width: "19%" }}>PAYMENT INFORMATION</th>
                                <th style={{ borderBottom: bdr, padding: "0.5mm 1mm", textAlign: "center", fontWeight: "bold", width: "16%" }}>PAYMENT METHOD</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{ borderRight: bdr, padding: "1mm 1mm", textAlign: "center" }}>{desc || modes[mode] || ""}</td>
                                <td style={{ borderRight: bdr, padding: "1mm 1mm", textAlign: "center" }}>{quantity}</td>
                                <td style={{ borderRight: bdr, padding: "1mm 1mm", textAlign: "center" }}>{netWeight || weight}</td>
                                <td style={{ borderRight: bdr, padding: "1mm 1mm", textAlign: "center" }}>{formatPrice(value)}</td>
                                <td style={{ borderRight: bdr, padding: "1mm 1mm", textAlign: "center" }}>{paymentMethods[paymentMethod] || ""}</td>
                                <td style={{ padding: "1mm 1mm", textAlign: "center" }}>{paymentUtilities[paymentUtility] || ""}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* ── ROW 4: SUMMARY ────────────────────────────────── */}
                <div style={{ display: "flex", borderBottom: bdr, height: "12mm", flexShrink: 0 }}>
                    <div style={{ flex: 1, borderRight: bdr, padding: "0.8mm 1.5mm", display: "flex", alignItems: "center", justifyContent: "center", gap: "1mm" }}>
                        <span style={{ fontSize: "13pt", lineHeight: 1 }}>⚖</span>
                        <div style={{ textAlign: "center" }}>
                            <div style={{ fontSize: "4.5pt", fontWeight: "bold" }}>TOTAL WEIGHT</div>
                            <div style={{ fontSize: "8pt", fontWeight: "bold" }}>{weight} Kg</div>
                        </div>
                    </div>
                    <div style={{ flex: 1.4, borderRight: bdr, padding: "0.8mm 1.5mm", display: "flex", alignItems: "center", justifyContent: "center", gap: "1mm" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="12pt" height="12pt" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M12 3l8 4.5l0 9l-8 4.5l-8 -4.5l0 -9l8 -4.5" />
                            <path d="M12 12l8 -4.5" />
                            <path d="M12 12l0 9" />
                            <path d="M12 12l-8 -4.5" />
                            <path d="M16 5.25l-8 4.5" />
                        </svg>
                        <div style={{ textAlign: "center" }}>
                            <div style={{ fontSize: "4.5pt", fontWeight: "bold" }}>DIMENSIONS (L x W x H)</div>
                            <div style={{ fontSize: "7pt", fontWeight: "bold", textAlign: "right" }}>Cm</div>
                        </div>
                    </div>
                    <div style={{ flex: 1, borderRight: bdr, padding: "0.8mm 1.5mm", display: "flex", alignItems: "center", justifyContent: "center", gap: "1mm" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="11pt" height="11pt" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12" />
                            <path d="M16 3v4" />
                            <path d="M8 3v4" />
                            <path d="M4 11h16" />
                            <path d="M11 15h1" />
                            <path d="M12 15v3" />
                        </svg>
                        <div style={{ textAlign: "center" }}>
                            <div style={{ fontSize: "4.5pt", fontWeight: "bold" }}>SHIPPING DATE</div>
                            <div style={{ fontSize: "6.5pt", fontWeight: "bold" }}>{departTime}</div>
                        </div>
                    </div>
                </div>

                {/* ── FOOTER ────────────────────────────────────────── */}
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-around", padding: "0 3mm", fontSize: "7pt" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "1mm" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="9pt" height="9pt" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M5 4h4l2 5l-2.5 1.5a11 11 0 0 0 5 5l1.5 -2.5l5 2v4a2 2 0 0 1 -2 2a16 16 0 0 1 -15 -15a2 2 0 0 1 2 -2" />
                            <path d="M15 7a2 2 0 0 1 2 2" />
                            <path d="M15 3a6 6 0 0 1 6 6" />
                        </svg>
                        <strong>8807-7555, 7100-7555, 010-9802-7555</strong>
                    </span>
                    <span style={{ borderLeft: bdr, height: "5mm", display: "inline-block" }} />
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "1mm" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="9pt" height="9pt" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10" />
                            <path d="M3 7l9 6l9 -6" />
                        </svg>
                        <strong>khishigtrade@gmail.com</strong>
                    </span>
                    <span style={{ borderLeft: bdr, height: "5mm", display: "inline-block" }} />
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "1mm" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="9pt" height="9pt" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M7 10v4h3v7h4v-7h3l1 -4h-4v-2a1 1 0 0 1 1 -1h3v-4h-3a5 5 0 0 0 -5 5v2h-3" />
                        </svg>
                        <strong>Khishig Cargo LLC</strong>
                    </span>
                </div>
            </div>
            </div>
        </>
    );
}

export default PrintEN;
